/**
 * Admin dashboard — protected by Supabase Auth (email magic link).
 * Tabs: Fix Queue | Suggestions | Search & Edit | Add Entry
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { Session } from '@supabase/supabase-js'
import type { Suggestion, ReviewStatus } from '../types'
import { SUGGESTION_TYPE_LABELS } from '../types'

// ── Auth gate ─────────────────────────────────────────────────

export function AdminPage() {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => subscription.unsubscribe()
  }, [])

  if (loading) return <Centered>Loading…</Centered>
  if (!session) return <LoginForm />
  return <AdminDashboard session={session} />
}

// ── Login ─────────────────────────────────────────────────────

function LoginForm() {
  const [email, setEmail]     = useState('')
  const [sent, setSent]       = useState(false)
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } })
    setLoading(false)
    if (error) setError(error.message)
    else setSent(true)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 max-w-sm w-full space-y-6">
        <div>
          <h1 className="text-xl font-bold text-tamang-700">Admin Login</h1>
          <p className="text-sm text-stone-500 mt-1">Enter your admin email to receive a sign-in link.</p>
        </div>
        {sent ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
            ✅ Magic link sent to <strong>{email}</strong>. Check your inbox.
          </div>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Email address</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="admin@example.com" required
                className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600"
              />
            </div>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button type="submit" className="btn-primary w-full justify-center" disabled={loading}>
              {loading ? 'Sending…' : 'Send magic link'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Dashboard shell ───────────────────────────────────────────

type Tab = 'fixqueue' | 'suggestions' | 'search' | 'add' | 'merge'

function AdminDashboard({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>('fixqueue')
  const signOut = () => supabase.auth.signOut()

  const tabs: { id: Tab; label: string }[] = [
    { id: 'fixqueue',    label: '🚩 Fix Queue'     },
    { id: 'suggestions', label: '📬 Suggestions'   },
    { id: 'search',      label: '🔍 Search & Edit' },
    { id: 'add',         label: '➕ Add Entry'      },
    { id: 'merge',       label: '🔀 Merge'          },
  ]

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-tamang-700 text-white px-4 py-4 shadow-md">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Tamang Dictionary Admin</h1>
            <p className="text-sm text-white/70">{session.user.email}</p>
          </div>
          <button onClick={signOut} className="text-sm text-white/70 hover:text-white underline">
            Sign out
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 flex gap-1 pt-2">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors
                ${tab === t.id
                  ? 'border-tamang-600 text-tamang-700 bg-tamang-50'
                  : 'border-transparent text-stone-500 hover:text-stone-700 hover:bg-stone-50'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {tab === 'fixqueue'    && <FixQueueTab />}
        {tab === 'suggestions' && <SuggestionsTab session={session} />}
        {tab === 'search'      && <SearchEditTab />}
        {tab === 'add'         && <AddEntryTab />}
        {tab === 'merge'       && <MergeTab />}
      </main>
    </div>
  )
}

// ── Fix Queue ─────────────────────────────────────────────────
// Entries with the most downvotes — sorted by (down - up) descending.

interface FlaggedEntry {
  entry_id:                 string
  tamang_form_surface:      string
  nepali_form:              string
  english_gloss_auto:       string
  english_gloss_candidates: string
  romanized_tamang:         string
  ipa_tamang:               string
  frequency:                number
  status:                   string
  native_speaker_verified:  boolean
  up_count:                 number
  down_count:               number
  auto_flagged:             boolean
}

function FixQueueTab() {
  const [entries, setEntries]   = useState<FlaggedEntry[]>([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState('')

  useEffect(() => {
    setLoading(true)
    supabase
      .from('flagged_entries')
      .select('entry_id,tamang_form_surface,nepali_form,english_gloss_auto,english_gloss_candidates,romanized_tamang,ipa_tamang,frequency,status,native_speaker_verified,up_count,down_count')
      .limit(100)
      .then(({ data, error: err }) => {
        setLoading(false)
        if (err) setError(err.message)
        else setEntries((data ?? []) as FlaggedEntry[])
      })
  }, [])

  const onSaved = (entry_id: string) => {
    setEntries(prev => prev.filter(e => e.entry_id !== entry_id))
  }

  if (loading) return <p className="text-stone-400 animate-pulse">Loading flagged entries…</p>
  if (error)   return <p className="text-rose-600">Error: {error}</p>

  if (entries.length === 0) return (
    <div className="text-center py-20 text-stone-400">
      <p className="text-4xl mb-3">✅</p>
      <p className="text-lg">No downvoted entries right now.</p>
    </div>
  )

  const autoFlagged = entries.filter(e => e.auto_flagged)
  const regular     = entries.filter(e => !e.auto_flagged)

  return (
    <div className="space-y-6">
      <p className="text-sm text-stone-500">
        {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} flagged — sorted by most downvoted first.
      </p>

      {/* Auto-flagged section: 10+ downvotes */}
      {autoFlagged.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-red-600 text-lg">🔴</span>
            <h2 className="font-semibold text-red-700">
              Needs urgent review — {autoFlagged.length} entr{autoFlagged.length === 1 ? 'y' : 'ies'} with 10+ downvotes
            </h2>
          </div>
          <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 space-y-3">
            {autoFlagged.map(e => (
              <EditableEntryCard key={e.entry_id} entry={e} onSaved={onSaved} />
            ))}
          </div>
        </div>
      )}

      {/* Regular downvoted entries */}
      {regular.length > 0 && (
        <div className="space-y-3">
          {autoFlagged.length > 0 && (
            <h2 className="font-semibold text-stone-600">Other downvoted entries</h2>
          )}
          {regular.map(e => (
            <EditableEntryCard key={e.entry_id} entry={e} onSaved={onSaved} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Search & Edit ─────────────────────────────────────────────
// Find any entry by Tamang, Nepali, or English and edit it directly.

function SearchEditTab() {
  const [query,   setQuery]   = useState('')
  const [results, setResults] = useState<FlaggedEntry[]>([])
  const [loading, setLoading] = useState(false)

  const search = useCallback(async () => {
    if (!query.trim()) return
    setLoading(true)
    const { data } = await supabase.rpc('search_entries', {
      search_term: query.trim(),
      page_offset: 0,
      page_limit:  20,
    })
    setLoading(false)
    setResults((data ?? []).map((r: Record<string, unknown>) => ({
      entry_id:                 r.entry_id,
      tamang_form_surface:      r.tamang_form_surface,
      nepali_form:              r.nepali_form,
      english_gloss_auto:       r.english_gloss_auto,
      english_gloss_candidates: '',
      romanized_tamang:         r.romanized_tamang ?? '',
      ipa_tamang:               r.ipa_tamang ?? '',
      frequency:                r.frequency,
      status:                   r.status,
      native_speaker_verified:  false,
      up_count:                 0,
      down_count:               0,
    })))
  }, [query])

  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') search() }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search Tamang, Nepali, or English…"
          className="flex-1 rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600"
        />
        <button onClick={search} className="btn-primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {results.length > 0 && (
        <div className="space-y-4">
          {results.map(e => (
            <EditableEntryCard key={e.entry_id} entry={e} onSaved={() => {}} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Editable entry card ───────────────────────────────────────
// Shared by FixQueueTab and SearchEditTab.

function EditableEntryCard({
  entry,
  onSaved,
}: {
  entry: FlaggedEntry
  onSaved: (entry_id: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [tamang,    setTamang]    = useState(entry.tamang_form_surface)
  const [nepali,    setNepali]    = useState(entry.nepali_form)
  const [english,   setEnglish]   = useState(entry.english_gloss_auto)
  const [romanized, setRomanized] = useState(entry.romanized_tamang ?? '')
  const [ipa,       setIpa]       = useState(entry.ipa_tamang ?? '')
  const [saving,   setSaving]   = useState(false)
  const [saved,    setSaved]    = useState(false)
  const [error,    setError]    = useState('')

  const isDirty = tamang    !== entry.tamang_form_surface
    || nepali    !== entry.nepali_form
    || english   !== entry.english_gloss_auto
    || romanized !== (entry.romanized_tamang ?? '')
    || ipa       !== (entry.ipa_tamang ?? '')

  const handleSave = async (markVerified: boolean) => {
    setSaving(true)
    setError('')
    const { error: err } = await supabase
      .from('dictionary_entries')
      .update({
        tamang_form_surface:  tamang,
        nepali_form:          nepali,
        english_gloss_auto:   english,
        romanized_tamang:     romanized,
        ipa_tamang:           ipa,
        ...(markVerified ? { native_speaker_verified: true, verified_at: new Date().toISOString() } : {}),
      })
      .eq('entry_id', entry.entry_id)

    setSaving(false)
    if (err) {
      setError(err.message)
    } else {
      setSaved(true)
      setTimeout(() => onSaved(entry.entry_id), 800)
    }
  }

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors
      ${entry.native_speaker_verified ? 'border-emerald-200' : 'border-stone-200'}`}>

      {/* Summary row */}
      <div
        className="flex items-start justify-between gap-4 p-4 cursor-pointer hover:bg-stone-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-stone-400">{entry.entry_id}</span>
            {entry.auto_flagged && (
              <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                🔴 10+ downvotes
              </span>
            )}
            {entry.native_speaker_verified && (
              <span className="text-xs font-semibold text-emerald-600">✓ Verified</span>
            )}
          </div>
          <p className="deva text-xl font-bold text-tamang-700">{entry.tamang_form_surface}</p>
          <p className="deva text-stone-600">{entry.nepali_form}</p>
          {entry.english_gloss_auto && (
            <p className="text-stone-400 italic text-sm">{entry.english_gloss_auto}</p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0 text-xs">
          {(entry.down_count > 0 || entry.up_count > 0) && (
            <div className="flex gap-2">
              <span className="text-emerald-600 font-medium">👍 {entry.up_count}</span>
              <span className="text-rose-600 font-medium">👎 {entry.down_count}</span>
            </div>
          )}
          <span className="text-stone-400">freq {entry.frequency.toLocaleString()}</span>
          <span className="text-stone-400">{expanded ? '▲ collapse' : '▼ edit'}</span>
        </div>
      </div>

      {/* Edit form */}
      {expanded && (
        <div className="border-t border-stone-100 p-4 space-y-4 bg-stone-50">

          {/* Candidate glosses hint */}
          {entry.english_gloss_candidates && (
            <div className="text-xs text-stone-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <strong>Machine gloss candidates:</strong> {entry.english_gloss_candidates}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                Tamang form
              </label>
              <input value={tamang} onChange={e => setTamang(e.target.value)}
                className="w-full rounded-lg border-stone-300 text-sm deva focus:ring-tamang-600 focus:border-tamang-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                Romanized Tamang
              </label>
              <input value={romanized} onChange={e => setRomanized(e.target.value)}
                placeholder="e.g. min"
                className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                IPA
              </label>
              <input value={ipa} onChange={e => setIpa(e.target.value)}
                placeholder="e.g. mɪn"
                className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                Nepali form
              </label>
              <input value={nepali} onChange={e => setNepali(e.target.value)}
                className="w-full rounded-lg border-stone-300 text-sm deva focus:ring-tamang-600 focus:border-tamang-600" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                English gloss
              </label>
              <input value={english} onChange={e => setEnglish(e.target.value)}
                className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600" />
            </div>
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
          {saved  && <p className="text-sm text-emerald-600">✓ Saved — removing from queue…</p>}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleSave(true)}
              disabled={saving || saved}
              className="btn-primary bg-emerald-600 hover:bg-emerald-500 focus:ring-emerald-600 disabled:opacity-50"
            >
              {saving ? 'Saving…' : '✓ Save & mark verified'}
            </button>
            {isDirty && (
              <button
                onClick={() => handleSave(false)}
                disabled={saving || saved}
                className="btn-secondary disabled:opacity-50"
              >
                Save without verifying
              </button>
            )}
            <button
              onClick={() => setExpanded(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Suggestions tab ───────────────────────────────────────────

type FilterStatus = ReviewStatus | 'all'

function SuggestionsTab({ session }: { session: Session }) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [filter,  setFilter]  = useState<FilterStatus>('pending')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')

  const fetchSuggestions = useCallback(async () => {
    setLoading(true)
    setError('')
    let q = supabase.from('suggestions').select('*').order('submitted_at', { ascending: false })
    if (filter !== 'all') q = q.eq('review_status', filter)
    const { data, error: err } = await q
    setLoading(false)
    if (err) setError(err.message)
    else setSuggestions(data ?? [])
  }, [filter])

  useEffect(() => { fetchSuggestions() }, [fetchSuggestions])

  const updateStatus = async (id: string, status: ReviewStatus, adminNotes?: string) => {
    // Find the suggestion being acted on
    const suggestion = suggestions.find(s => s.id === id)

    // If accepting, apply the suggested changes to dictionary_entries
    if (status === 'accepted' && suggestion) {
      const entryUpdate: Record<string, unknown> = {
        native_speaker_verified: true,
        verified_at: new Date().toISOString(),
      }
      if (suggestion.corrected_tamang)       entryUpdate.tamang_form_surface  = suggestion.corrected_tamang
      if (suggestion.corrected_nepali)       entryUpdate.nepali_form          = suggestion.corrected_nepali
      if (suggestion.corrected_english)      entryUpdate.english_gloss_auto   = suggestion.corrected_english
      if (suggestion.suggested_romanization) entryUpdate.romanized_tamang     = suggestion.suggested_romanization

      await supabase
        .from('dictionary_entries')
        .update(entryUpdate)
        .eq('entry_id', suggestion.entry_id)
    }

    const { error } = await supabase
      .from('suggestions')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ review_status: status, reviewed_by: session.user.email ?? 'admin', reviewed_at: new Date().toISOString(), admin_notes: adminNotes ?? null } as any)
      .eq('id', id)
    if (!error) setSuggestions(s => s.filter(r => r.id !== id))
  }

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {(['all', 'pending', 'accepted', 'rejected', 'merged'] as FilterStatus[]).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? 'bg-tamang-700 text-white'
                : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading && <p className="text-stone-400 animate-pulse">Loading…</p>}
      {error   && <p className="text-rose-600">Error: {error}</p>}
      {!loading && suggestions.length === 0 && (
        <div className="text-center py-16 text-stone-400">
          <p className="text-3xl mb-3">✅</p>
          <p>No {filter === 'all' ? '' : filter} suggestions.</p>
        </div>
      )}

      <div className="space-y-4">
        {suggestions.map(s => (
          <SuggestionCard key={s.id} suggestion={s} onAction={updateStatus} />
        ))}
      </div>
    </div>
  )
}

// ── Suggestion card ───────────────────────────────────────────

function SuggestionCard({ suggestion: s, onAction }: {
  suggestion: Suggestion
  onAction: (id: string, status: ReviewStatus, notes?: string) => void
}) {
  const [notes, setNotes]     = useState('')
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
      <div
        className="flex items-start justify-between gap-4 p-4 cursor-pointer hover:bg-stone-50"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-stone-800 font-mono text-sm">{s.entry_id}</span>
            <span className="badge-candidate">{SUGGESTION_TYPE_LABELS[s.suggestion_type]}</span>
            <StatusPill status={s.review_status} />
          </div>
          {s.corrected_tamang && <p className="deva text-tamang-700 font-medium">{s.corrected_tamang}</p>}
          {s.submitter_name   && <p className="text-sm text-stone-500">From: {s.submitter_name}</p>}
        </div>
        <div className="text-xs text-stone-400 shrink-0 text-right">
          <p>{new Date(s.submitted_at).toLocaleDateString()}</p>
          <p>{expanded ? '▲' : '▼'}</p>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-stone-100 p-4 space-y-4">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Field label="Corrected Tamang"       value={s.corrected_tamang}       deva />
            <Field label="Corrected Nepali"       value={s.corrected_nepali}       deva />
            <Field label="Corrected English"      value={s.corrected_english} />
            <Field label="Romanized Tamang"       value={s.suggested_romanization} />
            <Field label="Dialect note"           value={s.dialect_note}           deva />
            <Field label="Speaker notes"          value={s.speaker_notes} />
            <Field label="Submitter email"        value={s.submitter_email} />
          </dl>
          {s.review_status === 'pending' && (
            <div className="space-y-2">
              <textarea
                value={notes} onChange={e => setNotes(e.target.value)}
                rows={2} placeholder="Internal notes (optional)…"
                className="w-full rounded-lg border-stone-300 text-sm"
              />
              <div className="flex gap-2">
                <button className="btn-primary bg-emerald-600 hover:bg-emerald-500 focus:ring-emerald-600"
                  onClick={() => onAction(s.id, 'accepted', notes)}>✓ Accept</button>
                <button className="btn-secondary text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => onAction(s.id, 'rejected', notes)}>✗ Reject</button>
                <button className="btn-secondary"
                  onClick={() => onAction(s.id, 'merged', notes)}>↩ Mark merged</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Add Entry Tab ─────────────────────────────────────────────

const ENTRY_TYPES = [
  { value: 'lexical_candidate', label: 'Lexical word'    },
  { value: 'grammar_marker',    label: 'Grammar marker'  },
  { value: 'phrase_candidate',  label: 'Phrase'          },
  { value: 'proper_noun',       label: 'Proper noun'     },
  { value: 'uncertain',         label: 'Uncertain'       },
]

function generateEntryId(tamang: string, nepali: string): string {
  const raw = `${tamang.trim()}|${nepali.trim()}`
  let hash = 0
  for (let i = 0; i < raw.length; i++) {
    hash = (Math.imul(31, hash) + raw.charCodeAt(i)) | 0
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0').slice(0, 8)
  return `dict_${hex}`
}

function AddEntryTab() {
  const [tamang,    setTamang]    = useState('')
  const [nepali,    setNepali]    = useState('')
  const [english,   setEnglish]   = useState('')
  const [romanized, setRomanized] = useState('')
  const [ipa,       setIpa]       = useState('')
  const [entryType, setEntryType] = useState('lexical_candidate')
  const [ex1t,      setEx1t]      = useState('')
  const [ex1n,      setEx1n]      = useState('')
  const [ex2t,      setEx2t]      = useState('')
  const [ex2n,      setEx2n]      = useState('')
  const [ex3t,      setEx3t]      = useState('')
  const [ex3n,      setEx3n]      = useState('')
  const [saving,    setSaving]    = useState(false)
  const [saved,     setSaved]     = useState(false)
  const [error,     setError]     = useState('')

  const reset = () => {
    setTamang(''); setNepali(''); setEnglish(''); setRomanized(''); setIpa(''); setEntryType('lexical_candidate')
    setEx1t(''); setEx1n(''); setEx2t(''); setEx2n(''); setEx3t(''); setEx3n('')
    setSaved(false); setError('')
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tamang.trim() || !nepali.trim()) {
      setError('Tamang and Nepali forms are required.')
      return
    }
    setSaving(true)
    setError('')

    const entry_id = generateEntryId(tamang, nepali)

    const { error: err } = await supabase.from('dictionary_entries').insert({
      entry_id,
      entry_type:              entryType,
      tamang_form_surface:     tamang.trim(),
      tamang_form_normalized:  tamang.trim(),
      tamang_variant_note:     '',
      nepali_form:             nepali.trim(),
      english_gloss_auto:      english.trim(),
      romanized_tamang:        romanized.trim(),
      ipa_tamang:              ipa.trim(),
      english_gloss_candidates:'',
      frequency:               1,
      example_tamang_1:        ex1t.trim(),
      example_nepali_1:        ex1n.trim(),
      example_english_1_auto:  '',
      example_sentence_id_1:   '',
      example_tamang_2:        ex2t.trim(),
      example_nepali_2:        ex2n.trim(),
      example_english_2_auto:  '',
      example_sentence_id_2:   '',
      example_tamang_3:        ex3t.trim(),
      example_nepali_3:        ex3n.trim(),
      example_english_3_auto:  '',
      example_sentence_id_3:   '',
      alignment_sources:       'manual',
      has_position_support:    false,
      has_eflomal_support:     false,
      has_tamang_neural_support: false,
      alignment_consistency_score: null,
      has_true_roundtrip_score: false,
      status:                  'candidate_high_priority',
      native_speaker_verified: true,
      verified_at:             new Date().toISOString(),
    })

    setSaving(false)
    if (err) {
      setError(err.message)
    } else {
      setSaved(true)
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-stone-500">
        Add a word that is missing from the dictionary. Entries added here are marked
        <strong> Native Speaker Verified</strong> automatically.
      </p>

      {saved ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center space-y-3">
          <p className="text-emerald-700 font-semibold text-lg">✓ Entry added successfully</p>
          <p className="text-sm text-stone-500 font-mono">{generateEntryId(tamang, nepali)}</p>
          <div className="flex gap-3 justify-center">
            <button onClick={reset} className="btn-primary">Add another word</button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-6">

          {/* Core fields */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
            <h2 className="font-semibold text-stone-700">Word forms</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                  Tamang form <span className="text-rose-500">*</span>
                </label>
                <input
                  value={tamang} onChange={e => setTamang(e.target.value)} required
                  placeholder="e.g. मुला"
                  className="w-full rounded-lg border-stone-300 text-sm deva focus:ring-tamang-600 focus:border-tamang-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                  Nepali form <span className="text-rose-500">*</span>
                </label>
                <input
                  value={nepali} onChange={e => setNepali(e.target.value)} required
                  placeholder="e.g. छ"
                  className="w-full rounded-lg border-stone-300 text-sm deva focus:ring-tamang-600 focus:border-tamang-600"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                  Romanized Tamang
                </label>
                <input
                  value={romanized} onChange={e => setRomanized(e.target.value)}
                  placeholder="e.g. min"
                  className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                  IPA
                </label>
                <input
                  value={ipa} onChange={e => setIpa(e.target.value)}
                  placeholder="e.g. mɪn"
                  className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                  English gloss
                </label>
                <input
                  value={english} onChange={e => setEnglish(e.target.value)}
                  placeholder="e.g. is (present tense)"
                  className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">
                  Entry type
                </label>
                <select
                  value={entryType} onChange={e => setEntryType(e.target.value)}
                  className="w-full rounded-lg border-stone-300 text-sm focus:ring-tamang-600 focus:border-tamang-600"
                >
                  {ENTRY_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Example sentences */}
          <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
            <h2 className="font-semibold text-stone-700">Example sentences <span className="text-stone-400 font-normal text-sm">(optional)</span></h2>

            {[
              { t: ex1t, setT: setEx1t, n: ex1n, setN: setEx1n, label: 'Example 1' },
              { t: ex2t, setT: setEx2t, n: ex2n, setN: setEx2n, label: 'Example 2' },
              { t: ex3t, setT: setEx3t, n: ex3n, setN: setEx3n, label: 'Example 3' },
            ].map(({ t, setT, n, setN, label }) => (
              <div key={label} className="space-y-2">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide">{label}</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input value={t} onChange={e => setT(e.target.value)}
                    placeholder="Tamang sentence…"
                    className="rounded-lg border-stone-300 text-sm deva focus:ring-tamang-600 focus:border-tamang-600" />
                  <input value={n} onChange={e => setN(e.target.value)}
                    placeholder="Nepali sentence…"
                    className="rounded-lg border-stone-300 text-sm deva focus:ring-tamang-600 focus:border-tamang-600" />
                </div>
              </div>
            ))}
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}

          <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : '➕ Add to dictionary'}
          </button>
        </form>
      )}
    </div>
  )
}

// ── Merge Tab ─────────────────────────────────────────────────

interface EntryPreview {
  entry_id: string
  tamang_form_surface: string
  nepali_form: string
  english_gloss_auto: string
  romanized_tamang: string
  frequency: number
}

function DeduplicateSection() {
  const [count,   setCount]   = useState<number | null>(null)
  const [running, setRunning] = useState(false)
  const [result,  setResult]  = useState<number | null>(null)
  const [error,   setError]   = useState('')

  useEffect(() => {
    supabase.rpc('count_duplicates').then(({ data }) => setCount(data ?? 0))
  }, [])

  const run = async () => {
    setRunning(true); setError(''); setResult(null)
    const { data, error: err } = await supabase.rpc('deduplicate_entries')
    setRunning(false)
    if (err) setError(err.message)
    else { setResult(data); setCount(0) }
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3">
      <h2 className="font-semibold text-amber-800">Auto-deduplicate all entries</h2>
      <p className="text-sm text-amber-700">
        Finds entries with identical Tamang + Nepali forms, keeps the oldest one,
        merges votes/suggestions and fields, then deletes duplicates.
      </p>

      {count !== null && count > 0 && (
        <p className="text-sm font-semibold text-amber-800">
          {count.toLocaleString()} duplicate entries detected.
        </p>
      )}
      {count === 0 && (
        <p className="text-sm text-emerald-700 font-semibold">✓ No duplicates found.</p>
      )}
      {result !== null && (
        <p className="text-sm text-emerald-700 font-semibold">✓ Deleted {result.toLocaleString()} duplicates.</p>
      )}
      {error && <p className="text-sm text-rose-600">{error}</p>}

      {(count === null || count > 0) && result === null && (
        <button
          onClick={run}
          disabled={running || count === 0}
          className="btn-primary bg-amber-600 hover:bg-amber-500 focus:ring-amber-600 disabled:opacity-50"
        >
          {running ? 'Running…' : `Remove ${count !== null ? count.toLocaleString() : '…'} duplicates`}
        </button>
      )}
    </div>
  )
}

function MergeTab() {
  const [keepId,    setKeepId]    = useState('')
  const [deleteId,  setDeleteId]  = useState('')
  const [keepEntry,   setKeepEntry]   = useState<EntryPreview | null>(null)
  const [deleteEntry, setDeleteEntry] = useState<EntryPreview | null>(null)
  const [loadingK,  setLoadingK]  = useState(false)
  const [loadingD,  setLoadingD]  = useState(false)
  const [merging,   setMerging]   = useState(false)
  const [done,      setDone]      = useState(false)
  const [error,     setError]     = useState('')

  const fetchEntry = async (id: string, which: 'keep' | 'delete') => {
    if (!id.trim()) return
    which === 'keep' ? setLoadingK(true) : setLoadingD(true)
    const { data, error: err } = await supabase
      .from('dictionary_entries')
      .select('entry_id,tamang_form_surface,nepali_form,english_gloss_auto,romanized_tamang,frequency')
      .eq('entry_id', id.trim())
      .maybeSingle()
    which === 'keep' ? setLoadingK(false) : setLoadingD(false)
    if (err || !data) {
      if (which === 'keep') setKeepEntry(null)
      else setDeleteEntry(null)
      setError(`Entry "${id}" not found.`)
    } else {
      setError('')
      if (which === 'keep') setKeepEntry(data as EntryPreview)
      else setDeleteEntry(data as EntryPreview)
    }
  }

  const handleMerge = async () => {
    if (!keepEntry || !deleteEntry) return
    setMerging(true)
    setError('')
    const { error: err } = await supabase.rpc('merge_entries', {
      keep_id:   keepEntry.entry_id,
      delete_id: deleteEntry.entry_id,
    })
    setMerging(false)
    if (err) setError(err.message)
    else setDone(true)
  }

  if (done) return (
    <div className="text-center py-20 space-y-3">
      <p className="text-4xl">✅</p>
      <p className="text-lg font-semibold text-stone-700">Merged successfully</p>
      <p className="text-sm text-stone-500">
        <strong>{deleteEntry?.entry_id}</strong> was merged into <strong>{keepEntry?.entry_id}</strong> and deleted.
      </p>
      <button className="btn-secondary mt-4" onClick={() => {
        setKeepId(''); setDeleteId(''); setKeepEntry(null); setDeleteEntry(null); setDone(false)
      }}>
        Merge another
      </button>
    </div>
  )

  return (
    <div className="max-w-2xl space-y-6">
      <DeduplicateSection />

      <p className="text-sm text-stone-500">
        Or manually merge two specific entries by ID:
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Keep */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide">
            Keep this entry
          </label>
          <div className="flex gap-2">
            <input
              value={keepId} onChange={e => setKeepId(e.target.value)}
              placeholder="dict_000001"
              className="flex-1 rounded-lg border-stone-300 text-sm font-mono focus:ring-tamang-600 focus:border-tamang-600"
            />
            <button onClick={() => fetchEntry(keepId, 'keep')} className="btn-secondary" disabled={loadingK}>
              {loadingK ? '…' : 'Look up'}
            </button>
          </div>
          {keepEntry && <EntryPreviewCard entry={keepEntry} color="emerald" label="KEEP" />}
        </div>

        {/* Delete */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide">
            Delete this duplicate
          </label>
          <div className="flex gap-2">
            <input
              value={deleteId} onChange={e => setDeleteId(e.target.value)}
              placeholder="dict_8b04ec34"
              className="flex-1 rounded-lg border-stone-300 text-sm font-mono focus:ring-tamang-600 focus:border-tamang-600"
            />
            <button onClick={() => fetchEntry(deleteId, 'delete')} className="btn-secondary" disabled={loadingD}>
              {loadingD ? '…' : 'Look up'}
            </button>
          </div>
          {deleteEntry && <EntryPreviewCard entry={deleteEntry} color="rose" label="DELETE" />}
        </div>
      </div>

      {error && <p className="text-sm text-rose-600">{error}</p>}

      {keepEntry && deleteEntry && (
        <button
          onClick={handleMerge}
          disabled={merging}
          className="btn-primary bg-rose-600 hover:bg-rose-500 focus:ring-rose-600 disabled:opacity-50"
        >
          {merging ? 'Merging…' : `🔀 Merge — keep ${keepEntry.entry_id}, delete ${deleteEntry.entry_id}`}
        </button>
      )}
    </div>
  )
}

function EntryPreviewCard({ entry, color, label }: {
  entry: EntryPreview
  color: 'emerald' | 'rose'
  label: string
}) {
  const border = color === 'emerald' ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
  const badge  = color === 'emerald' ? 'bg-emerald-600' : 'bg-rose-600'
  return (
    <div className={`rounded-xl border p-3 space-y-1 text-sm ${border}`}>
      <div className="flex items-center gap-2">
        <span className={`text-white text-xs font-bold px-2 py-0.5 rounded ${badge}`}>{label}</span>
        <span className="font-mono text-xs text-stone-500">{entry.entry_id}</span>
      </div>
      <p className="deva text-lg font-bold text-tamang-700">{entry.tamang_form_surface}</p>
      {entry.romanized_tamang && <p className="text-stone-600">{entry.romanized_tamang}</p>}
      <p className="deva text-stone-600">{entry.nepali_form}</p>
      {entry.english_gloss_auto && <p className="text-stone-400 italic">{entry.english_gloss_auto}</p>}
      <p className="text-stone-400 text-xs">freq {entry.frequency.toLocaleString()}</p>
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────

function Field({ label, value, deva }: { label: string; value: string | null; deva?: boolean }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-xs text-stone-400 uppercase tracking-wide">{label}</dt>
      <dd className={`mt-0.5 text-stone-700 ${deva ? 'deva' : ''}`}>{value}</dd>
    </div>
  )
}

function StatusPill({ status }: { status: ReviewStatus }) {
  const cls =
    status === 'accepted' ? 'badge-high-priority' :
    status === 'rejected' ? 'badge-needs-review'  :
    status === 'merged'   ? 'badge-unverified'     :
    'bg-stone-100 text-stone-600 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold'
  return <span className={cls}>{status}</span>
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center text-stone-400">{children}</div>
}
